/**
 * useDailyHealth Hook
 *
 * Fetches daily health aggregations for uptime tracking.
 * Returns last 90 days of daily health data from Firestore.
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from 'config/firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';

// Number of days to fetch
const DEFAULT_DAYS = 90;

/**
 * Calculate uptime percentages from daily data
 * @param {Array} dailyData - Array of daily health documents
 * @param {number} days - Number of days to consider
 * @returns {Object} Uptime stats
 */
function calculateUptimeStats(dailyData, days = 90) {
  if (!dailyData || dailyData.length === 0) {
    return {
      today: null,
      last7Days: null,
      last90Days: null
    };
  }

  // Sort by date descending (most recent first)
  const sorted = [...dailyData].sort((a, b) => b.date.localeCompare(a.date));

  // Today (most recent day)
  const today = sorted[0]?.uptimePercent ?? null;

  // Last 7 days
  const last7 = sorted.slice(0, 7);
  let last7Days = null;
  if (last7.length > 0) {
    const totalChecks7 = last7.reduce((sum, d) => sum + (d.totalChecks || 0), 0);
    const greenYellow7 = last7.reduce((sum, d) => sum + (d.greenChecks || 0) + (d.yellowChecks || 0), 0);
    last7Days = totalChecks7 > 0
      ? Math.round((greenYellow7 / totalChecks7) * 1000) / 10
      : null;
  }

  // Last 90 days
  const last90 = sorted.slice(0, days);
  let last90Days = null;
  if (last90.length > 0) {
    const totalChecks90 = last90.reduce((sum, d) => sum + (d.totalChecks || 0), 0);
    const greenYellow90 = last90.reduce((sum, d) => sum + (d.greenChecks || 0) + (d.yellowChecks || 0), 0);
    last90Days = totalChecks90 > 0
      ? Math.round((greenYellow90 / totalChecks90) * 1000) / 10
      : null;
  }

  return {
    today,
    last7Days,
    last90Days
  };
}

/**
 * Calculate per-service uptime from daily data
 * @param {Array} dailyData - Array of daily health documents
 * @param {string} serviceId - Service to calculate for
 * @param {number} days - Number of days
 * @returns {Object} Service uptime stats
 */
function calculateServiceUptime(dailyData, serviceId, days = 30) {
  if (!dailyData || dailyData.length === 0) {
    return { uptimePercent: null, worstStatus: null, data: [] };
  }

  const sorted = [...dailyData].sort((a, b) => b.date.localeCompare(a.date));
  const relevantDays = sorted.slice(0, days);

  // Extract service-specific data
  const serviceData = relevantDays.map(day => {
    const serviceStats = day.services?.[serviceId];
    return {
      date: day.date,
      uptimePercent: serviceStats?.uptimePercent ?? null,
      worstStatus: serviceStats?.worstStatus ?? day.worstStatus ?? 'green',
      totalChecks: serviceStats?.totalChecks ?? 0,
      greenChecks: serviceStats?.greenChecks ?? 0,
      yellowChecks: serviceStats?.yellowChecks ?? 0,
      redChecks: serviceStats?.redChecks ?? 0
    };
  });

  // Calculate overall uptime for the period
  const totalChecks = serviceData.reduce((sum, d) => sum + d.totalChecks, 0);
  const greenYellow = serviceData.reduce((sum, d) => sum + d.greenChecks + d.yellowChecks, 0);
  const uptimePercent = totalChecks > 0
    ? Math.round((greenYellow / totalChecks) * 1000) / 10
    : null;

  // Find worst status
  let worstStatus = 'green';
  for (const day of serviceData) {
    if (day.worstStatus === 'red') {
      worstStatus = 'red';
      break;
    } else if (day.worstStatus === 'yellow' && worstStatus !== 'red') {
      worstStatus = 'yellow';
    }
  }

  return {
    uptimePercent,
    worstStatus,
    data: serviceData
  };
}

/**
 * Hook to fetch daily health data
 * @param {number} days - Number of days to fetch (default 90)
 * @returns {Object} { dailyData, uptimeStats, serviceUptime, loading, error, refetch }
 */
export function useDailyHealth(days = DEFAULT_DAYS) {
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDailyHealth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Query daily documents from systemHealth/daily/days collection
      const dailyRef = collection(db, 'systemHealth', 'daily', 'days');
      const dailyQuery = query(
        dailyRef,
        orderBy('date', 'desc'),
        limit(days)
      );

      const snapshot = await getDocs(dailyQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setDailyData(data);
    } catch (err) {
      console.error('Error fetching daily health data:', err);
      setError('Failed to load uptime history');
    } finally {
      setLoading(false);
    }
  }, [days]);

  // Initial fetch
  useEffect(() => {
    fetchDailyHealth();
  }, [fetchDailyHealth]);

  // Compute derived stats
  const uptimeStats = calculateUptimeStats(dailyData, days);

  // Helper to get service-specific uptime
  const getServiceUptime = useCallback((serviceId, serviceDays = 30) => {
    return calculateServiceUptime(dailyData, serviceId, serviceDays);
  }, [dailyData]);

  return {
    dailyData,
    uptimeStats,
    getServiceUptime,
    loading,
    error,
    refetch: fetchDailyHealth
  };
}

export default useDailyHealth;
